import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CollabBadge } from "@/components/CollabBadge";
import {
  FileBarChart2, Plus, Pencil, Trash2, X, Save, ArrowLeft,
  TrendingUp, TrendingDown, Minus, Search, Loader2, Info,
} from "lucide-react";

const PERIODS = [
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "quarterly", label: "Quarterly" },
];

// Fields we render as first-class metric inputs. Order matters — this drives
// both the form layout and the display cards. `kind` controls formatting:
//   int      → integer input, "1.2K" display
//   currency → USD input, "$1,234.56" display
//   percent  → 0-100 input with "%" suffix
//   hours    → decimal input, "3.2K hrs" display
const METRIC_FIELDS = [
  { key: "views",           label: "Views",           kind: "int" },
  { key: "subscribers",     label: "Subscribers",     kind: "int" },
  { key: "members",         label: "Members",         kind: "int" },
  { key: "followers",       label: "Followers",       kind: "int" },
  { key: "revenue",         label: "Revenue",         kind: "currency" },
  { key: "engagement_rate", label: "Engagement Rate", kind: "percent" },
  { key: "watch_time_hours",label: "Watch Time",      kind: "hours" },
  { key: "comments",        label: "Comments",        kind: "int" },
  { key: "shares",          label: "Shares",          kind: "int" },
];

function formatMetric(kind, val) {
  if (val === null || val === undefined || val === "") return "—";
  const n = Number(val);
  if (Number.isNaN(n)) return "—";
  if (kind === "currency") return "$" + n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (kind === "percent") return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  const suffix = kind === "hours" ? " hrs" : "";
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, "") + "M" + suffix;
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, "") + "K" + suffix;
  return n.toLocaleString() + suffix;
}

function todayISO() { return new Date().toISOString().slice(0, 10); }
const PERIOD_DAYS = { weekly: 7, monthly: 30, quarterly: 91 };
function periodStartISO(type) {
  const d = new Date();
  d.setDate(d.getDate() - (PERIOD_DAYS[type] || 30));
  return d.toISOString().slice(0, 10);
}

/** Top-level tab component: show-selector sidebar + reports panel on the right. */
export default function ReportCards() {
  const [summary, setSummary] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [selectedShowId, setSelectedShowId] = useState(null);
  const [query, setQuery] = useState("");

  const loadSummary = async () => {
    setLoadingSummary(true);
    try {
      const { data } = await api.get("/admin/reports/summary");
      setSummary(data.shows || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load summary");
    } finally {
      setLoadingSummary(false);
    }
  };
  useEffect(() => { loadSummary(); }, []);

  const filteredShows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return summary;
    return summary.filter((s) => (s.title || "").toLowerCase().includes(q));
  }, [summary, query]);

  return (
    <div className="space-y-8" data-testid="report-cards-page">
      <div>
        <div className="label-eyebrow text-[var(--ff-red)] mb-2">▮ ADMIN / INTERNAL REPORTING</div>
        <h2 className="font-display text-3xl mb-2">Show Report Cards</h2>
        <p className="text-[var(--ff-text-dim)] text-sm max-w-2xl">
          Time-series performance data per show — enter views, subs, members, revenue and any custom
          metric you like on a weekly / monthly / quarterly cadence. Everything here is admin-only.
        </p>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-px bg-[var(--ff-border)] min-h-[500px]">
        {/* Sidebar */}
        <div className="bg-[var(--ff-surface)] p-4 space-y-3" data-testid="report-cards-sidebar">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-dim)]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search shows…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-black/40 border border-[var(--ff-border)]"
              data-testid="report-cards-search"
            />
          </div>
          {loadingSummary ? (
            <div className="text-[var(--ff-text-dim)] text-xs font-mono-ui uppercase tracking-[0.18em] p-3 flex items-center gap-2">
              <Loader2 className="animate-spin" size={12} /> Loading shows…
            </div>
          ) : filteredShows.length === 0 ? (
            <div className="text-[var(--ff-text-dim)] text-xs p-3">No matching shows.</div>
          ) : (
            <div className="space-y-px max-h-[600px] overflow-auto" data-testid="report-cards-show-list">
              {filteredShows.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSelectedShowId(s.id)}
                  data-testid={`report-cards-show-${s.id}`}
                  className={`w-full text-left px-3 py-3 transition-colors ${
                    selectedShowId === s.id
                      ? "bg-[var(--ff-red)]/20 border-l-2 border-[var(--ff-red)]"
                      : "bg-black/20 hover:bg-black/40 border-l-2 border-transparent"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-mono-ui text-sm truncate">{s.title || "(Untitled)"}</div>
                      {s.category && <div className="text-[10px] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">{s.category}</div>}
                      {s.is_collab && <div className="mt-1"><CollabBadge size="xs" testId={`report-sidebar-collab-${s.id}`} /></div>}
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 text-[10px] font-mono-ui uppercase tracking-[0.15em] ${
                      s.count > 0 ? "bg-[var(--ff-red)]/20 text-[var(--ff-red)]" : "text-[var(--ff-text-dim)]"
                    }`}>
                      {s.count > 0 ? `${s.count} rpt` : "no rpts"}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right panel */}
        <div className="bg-[var(--ff-surface)] p-6" data-testid="report-cards-panel">
          {selectedShowId ? (
            <ShowReportsPanel
              key={selectedShowId}
              showId={selectedShowId}
              onDataChanged={loadSummary}
              onBack={() => setSelectedShowId(null)}
            />
          ) : (
            <EmptyPanel hasShows={summary.length > 0} />
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyPanel({ hasShows }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center py-20 space-y-3" data-testid="report-cards-empty-state">
      <FileBarChart2 className="text-[var(--ff-red)]" size={40} />
      <div className="font-display text-2xl">Pick a show to start reporting</div>
      <p className="text-sm text-[var(--ff-text-dim)] max-w-md">
        {hasShows
          ? "Select a show from the left to view its history or add a new report card."
          : "You haven't created any shows yet. Add shows first, then attach reports here."}
      </p>
    </div>
  );
}

/**
 * Panel showing a single show's reports (list + delta indicators) with a
 * button to open the form. Handles create/edit/delete flows.
 */
function ShowReportsPanel({ showId, onBack, onDataChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // report doc or null (new)
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/shows/${showId}/reports`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [showId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (r) => { setEditing(r); setFormOpen(true); };

  const doDelete = async (reportId) => {
    try {
      await api.delete(`/admin/reports/${reportId}`);
      toast.success("Report deleted");
      setConfirmDelete(null);
      await load();
      onDataChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  if (loading || !data) {
    return (
      <div className="p-10 text-center text-[var(--ff-text-dim)] flex items-center justify-center gap-2">
        <Loader2 className="animate-spin" size={16} /> Loading reports…
      </div>
    );
  }

  const reports = data.reports || [];
  return (
    <div className="space-y-6" data-testid="show-reports-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button onClick={onBack} className="label-eyebrow text-[var(--ff-text-dim)] hover:text-white transition-colors flex items-center gap-1 mb-2" data-testid="report-panel-back">
            <ArrowLeft size={12} /> All Shows
          </button>
          <div className="font-display text-3xl" data-testid="report-panel-show-title">{data.show?.title || "(Untitled)"}</div>
          <div className="text-xs text-[var(--ff-text-dim)] font-mono-ui uppercase tracking-[0.15em] mt-1">
            {reports.length} report{reports.length === 1 ? "" : "s"}
          </div>
        </div>
        <button onClick={openNew} className="btn-primary text-xs" data-testid="report-add-btn">
          <Plus size={14} className="inline mr-1" /> Add Report
        </button>
      </div>

      {reports.length === 0 ? (
        <div className="border border-[var(--ff-border)] border-dashed p-10 text-center" data-testid="show-reports-empty">
          <FileBarChart2 className="text-[var(--ff-text-dim)] mx-auto mb-3" size={32} />
          <div className="font-mono-ui text-sm text-[var(--ff-text-dim)] mb-4">
            No report cards yet for this show
          </div>
          <button onClick={openNew} className="btn-primary text-xs" data-testid="report-add-first-btn">
            <Plus size={14} className="inline mr-1" /> Create first report
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="show-reports-list">
          {reports.map((r, i) => (
            <ReportRow
              key={r.id}
              report={r}
              prev={reports[i + 1]}
              onEdit={() => openEdit(r)}
              onDelete={() => setConfirmDelete(r)}
            />
          ))}
        </div>
      )}

      {formOpen && (
        <ReportFormModal
          showId={showId}
          editing={editing}
          onClose={() => setFormOpen(false)}
          onSaved={async () => {
            setFormOpen(false);
            await load();
            onDataChanged?.();
          }}
        />
      )}
      {confirmDelete && (
        <ConfirmDelete
          report={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => doDelete(confirmDelete.id)}
        />
      )}
    </div>
  );
}

function ReportRow({ report, prev, onEdit, onDelete }) {
  const metrics = report.metrics || {};
  const prevMetrics = prev?.metrics || {};
  return (
    <div className="border border-[var(--ff-border)] p-5 bg-black/20" data-testid={`report-row-${report.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-mono-ui text-xs uppercase tracking-[0.18em] text-[var(--ff-red)]">
            {report.period_type}
          </div>
          <div className="font-display text-xl mt-1" data-testid={`report-period-${report.id}`}>
            {report.period_start} → {report.period_end}
          </div>
          {report.created_by && (
            <div className="text-[10px] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-1">
              by {report.created_by}
              {report.updated_at !== report.created_at && " · edited"}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={onEdit} className="p-2 hover:bg-black/40 transition-colors" title="Edit" data-testid={`report-edit-${report.id}`}>
            <Pencil size={14} />
          </button>
          <button onClick={onDelete} className="p-2 hover:bg-[var(--ff-red)]/20 text-[var(--ff-red)] transition-colors" title="Delete" data-testid={`report-delete-${report.id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-px bg-[var(--ff-border)]">
        {METRIC_FIELDS.map((f) => {
          const val = metrics[f.key];
          const prevVal = prevMetrics[f.key];
          if (val === undefined || val === null) return null;
          return (
            <MetricCell key={f.key} field={f} value={val} previous={prevVal} />
          );
        })}
      </div>

      {report.custom_fields?.length > 0 && (
        <div className="mt-4">
          <div className="label-eyebrow text-[var(--ff-text-dim)] mb-2">Custom Fields</div>
          <div className="flex flex-wrap gap-2">
            {report.custom_fields.map((c, i) => (
              <div key={i} className="px-3 py-2 bg-black/40 border border-[var(--ff-border)]" data-testid={`report-custom-${report.id}-${i}`}>
                <div className="text-[10px] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">{c.label}</div>
                <div className="text-sm">{c.value || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {report.notes && (
        <div className="mt-4">
          <div className="label-eyebrow text-[var(--ff-text-dim)] mb-2">Notes</div>
          <div className="text-sm text-[var(--ff-text-dim)] whitespace-pre-wrap bg-black/20 p-3 border-l-2 border-[var(--ff-red)]/40">{report.notes}</div>
        </div>
      )}
    </div>
  );
}

function MetricCell({ field, value, previous }) {
  const displayed = formatMetric(field.kind, value);
  let deltaBadge = null;
  if (previous !== undefined && previous !== null && Number(previous) !== 0) {
    const diff = Number(value) - Number(previous);
    const pct = (diff / Number(previous)) * 100;
    if (Math.abs(pct) >= 0.5) {
      const up = diff > 0;
      const flat = Math.abs(pct) < 1;
      const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
      const cls = flat ? "text-[var(--ff-text-dim)]" : up ? "text-emerald-400" : "text-[var(--ff-red)]";
      deltaBadge = (
        <span className={`inline-flex items-center gap-0.5 text-[10px] font-mono-ui ${cls}`}>
          <Icon size={10} />{up ? "+" : ""}{pct.toFixed(1)}%
        </span>
      );
    }
  }
  return (
    <div className="bg-[var(--ff-surface)] p-3">
      <div className="text-[10px] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">{field.label}</div>
      <div className="font-display text-xl mt-1 flex items-baseline gap-2">
        {displayed}
        {deltaBadge}
      </div>
    </div>
  );
}

function ReportFormModal({ showId, editing, onClose, onSaved }) {
  const isEdit = !!editing;
  const [periodType, setPeriodType] = useState(editing?.period_type || "monthly");
  const [periodStart, setPeriodStart] = useState(editing?.period_start || periodStartISO(editing?.period_type || "monthly"));
  const [periodEnd, setPeriodEnd] = useState(editing?.period_end || todayISO());
  const [metrics, setMetrics] = useState(() => {
    const m = editing?.metrics || {};
    const out = {};
    METRIC_FIELDS.forEach((f) => { out[f.key] = m[f.key] ?? ""; });
    return out;
  });
  const [customFields, setCustomFields] = useState(editing?.custom_fields || []);
  const [notes, setNotes] = useState(editing?.notes || "");
  const [saving, setSaving] = useState(false);

  const setMetric = (k, v) => setMetrics((prev) => ({ ...prev, [k]: v }));
  const addCustom = () => setCustomFields((prev) => [...prev, { label: "", value: "" }]);
  const setCustom = (i, key, v) => setCustomFields((prev) => prev.map((c, idx) => idx === i ? { ...c, [key]: v } : c));
  const rmCustom = (i) => setCustomFields((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (periodEnd < periodStart) {
      toast.error("Period end must be on or after period start");
      return;
    }
    setSaving(true);
    const payload = {
      period_type: periodType,
      period_start: periodStart,
      period_end: periodEnd,
      metrics: Object.fromEntries(
        Object.entries(metrics)
          .filter(([, v]) => v !== "" && v !== null && v !== undefined)
          .map(([k, v]) => [k, Number(v)])
      ),
      custom_fields: customFields.filter((c) => c.label.trim()),
      notes: notes.trim(),
    };
    try {
      if (isEdit) {
        await api.put(`/admin/reports/${editing.id}`, payload);
        toast.success("Report updated");
      } else {
        await api.post(`/admin/shows/${showId}/reports`, payload);
        toast.success("Report created");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="report-form-modal">
      <div className="w-full max-w-3xl bg-[var(--ff-surface)] border border-[var(--ff-border)] my-8">
        <div className="flex items-center justify-between p-5 border-b border-[var(--ff-border)]">
          <div>
            <div className="label-eyebrow text-[var(--ff-red)]">▮ {isEdit ? "EDIT" : "NEW"} REPORT CARD</div>
            <h3 className="font-display text-2xl mt-1">{isEdit ? "Edit Report" : "Add Report"}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/40" data-testid="report-form-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Period */}
          <div>
            <div className="label-eyebrow mb-2">Reporting Period</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">Cadence</label>
                <select value={periodType} onChange={(e) => { setPeriodType(e.target.value); if (!isEdit) setPeriodStart(periodStartISO(e.target.value)); }} className="w-full mt-1 p-2 bg-black/40 border border-[var(--ff-border)]" data-testid="report-period-type">
                  {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">Start Date</label>
                <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="w-full mt-1 p-2 bg-black/40 border border-[var(--ff-border)]" data-testid="report-period-start" />
              </div>
              <div>
                <label className="text-[10px] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">End Date</label>
                <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="w-full mt-1 p-2 bg-black/40 border border-[var(--ff-border)]" data-testid="report-period-end" />
              </div>
            </div>
          </div>

          {/* Metrics grid */}
          <div>
            <div className="label-eyebrow mb-2">Metrics <span className="text-[var(--ff-text-dim)] normal-case tracking-normal">(leave blank to skip)</span></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {METRIC_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="text-[10px] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
                    {f.label}{f.kind === "currency" && " ($)"}{f.kind === "percent" && " (%)"}{f.kind === "hours" && " (hrs)"}
                  </label>
                  <input
                    type="number"
                    step={f.kind === "int" ? "1" : "0.01"}
                    value={metrics[f.key]}
                    onChange={(e) => setMetric(f.key, e.target.value)}
                    placeholder="—"
                    className="w-full mt-1 p-2 bg-black/40 border border-[var(--ff-border)]"
                    data-testid={`report-metric-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Custom fields */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow">Custom Fields</div>
              <button onClick={addCustom} className="label-eyebrow text-[var(--ff-red)] hover:brightness-110 flex items-center gap-1" data-testid="report-add-custom">
                <Plus size={12} /> Add field
              </button>
            </div>
            {customFields.length === 0 && (
              <p className="text-xs text-[var(--ff-text-dim)] flex items-center gap-2">
                <Info size={12} /> Track anything the built-in metrics don&apos;t cover (e.g. &quot;Merch Sold&quot;, &quot;Live Attendance&quot;).
              </p>
            )}
            <div className="space-y-2">
              {customFields.map((c, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2" data-testid={`report-custom-row-${i}`}>
                  <input value={c.label} onChange={(e) => setCustom(i, "label", e.target.value)} placeholder="Label (e.g. Merch Sold)" className="p-2 bg-black/40 border border-[var(--ff-border)]" data-testid={`report-custom-label-${i}`} />
                  <input value={c.value} onChange={(e) => setCustom(i, "value", e.target.value)} placeholder="Value" className="p-2 bg-black/40 border border-[var(--ff-border)]" data-testid={`report-custom-value-${i}`} />
                  <button onClick={() => rmCustom(i)} className="p-2 text-[var(--ff-red)] hover:bg-black/40" title="Remove" data-testid={`report-custom-remove-${i}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="label-eyebrow mb-2 block">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything worth remembering about this reporting period…"
              className="w-full p-3 bg-black/40 border border-[var(--ff-border)] min-h-[100px]"
              data-testid="report-notes"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-[var(--ff-border)]">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 label-eyebrow text-[var(--ff-text-dim)] hover:text-white" data-testid="report-form-cancel">
            Cancel
          </button>
          <button onClick={submit} disabled={saving} className="btn-primary text-xs" data-testid="report-form-save">
            {saving ? <Loader2 className="inline animate-spin" size={12} /> : <Save size={12} className="inline mr-1" />}
            {saving ? " Saving…" : (isEdit ? "Save Changes" : "Create Report")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDelete({ report, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="report-confirm-delete">
      <div className="w-full max-w-md bg-[var(--ff-surface)] border border-[var(--ff-red)]/40 p-6">
        <h3 className="font-display text-xl mb-2">Delete this report?</h3>
        <p className="text-sm text-[var(--ff-text-dim)] mb-6">
          {report.period_type} report ({report.period_start} → {report.period_end}). This cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 label-eyebrow text-[var(--ff-text-dim)] hover:text-white" data-testid="report-delete-cancel">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 bg-[var(--ff-red)] text-white font-mono-ui text-xs uppercase tracking-[0.18em] hover:brightness-110" data-testid="report-delete-confirm">
            <Trash2 size={12} className="inline mr-1" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}
